import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import CommonButton from "../../components/common11/Buttons/CommonButton";
import CommonModal from "../../components/common11/commonModals/CommonModal";
import CommonCheckbox from "../../components/common11/formInputs/CommonCheckbox";
import SearchDropdown from "../../components/common11/formInputs/SearchDropdown";
import {
  DOCTOR_OPTIONS,
  LAB_ID_OPTIONS,
  PATHOLOGIST_OPTIONS,
  isOutOfRange,
} from "./pathologyResultApprovalData";

function PathologyResultEntryModal({
  isOpen,
  onClose,
  testRecord,
  onSave,
  labs = [],
  saving = false,
}) {
  const [parameters, setParameters] = useState([]);
  const [suggestionNote, setSuggestionNote] = useState("");
  const [labId, setLabId] = useState("");

  const labOptions = useMemo(() => {
    if (labs?.length) return labs;
    return LAB_ID_OPTIONS;
  }, [labs]);

  const { control, reset, getValues } = useForm({
    defaultValues: {
      pathologist: null,
      doctor: null,
      digitalSignRequired: false,
    },
  });

  useEffect(() => {
    if (!isOpen || !testRecord) return;
    setParameters((testRecord.parameters || []).map((p) => ({ ...p })));
    setSuggestionNote(testRecord.suggestionNote || "");
    const initialLab =
      testRecord.labId?.value ||
      testRecord.labId ||
      labOptions[0]?.value ||
      "";
    setLabId(initialLab);
    reset({
      pathologist: testRecord.pathologist || null,
      doctor: testRecord.doctor || null,
      digitalSignRequired: Boolean(testRecord.digitalSignRequired),
    });
  }, [isOpen, testRecord, reset, labOptions]);

  const handleResultChange = (paramId, value) => {
    setParameters((prev) =>
      prev.map((p) => (p.id === paramId ? { ...p, result: value } : p))
    );
  };

  const handleReset = () => {
    if (!testRecord) return;
    setParameters((testRecord.parameters || []).map((p) => ({ ...p })));
    setSuggestionNote(testRecord.suggestionNote || "");
    setLabId(
      testRecord.labId?.value ||
        testRecord.labId ||
        labOptions[0]?.value ||
        ""
    );
    reset({
      pathologist: testRecord.pathologist || null,
      doctor: testRecord.doctor || null,
      digitalSignRequired: Boolean(testRecord.digitalSignRequired),
    });
  };

  const handleSave = () => {
    if (!testRecord) return;
    const values = getValues();
    onSave({
      ...testRecord,
      parameters,
      suggestionNote,
      labId:
        labOptions.find((o) => o.value === labId) ||
        LAB_ID_OPTIONS.find((o) => o.value === labId) ||
        { id: labId, value: labId, label: labId },
      pathologist: values.pathologist,
      doctor: values.doctor,
      digitalSignRequired: Boolean(values.digitalSignRequired),
      reportStatus: "Completed",
    });
  };

  if (!testRecord) return null;

  const initial =
    testRecord.patientName?.trim()?.charAt(0)?.toUpperCase() || "P";

  return (
    <CommonModal
      isOpen={isOpen}
      onClose={onClose}
      title={testRecord.testName || "Result Entry"}
      size="xl"
      showCloseButton
      disableBackdropClose
      maxHeightVh={90}
      paperSx={{
        width: { xs: "calc(100% - 16px)", sm: "92vw", md: "88vw", lg: "82vw" },
        maxWidth: { xs: "calc(100vw - 16px)", sm: "1100px", lg: "1180px" },
        borderRadius: "12px",
      }}
      DialogContent={
        <div className="flex flex-col gap-3">
          <div className="shrink-0 rounded-lg border border-gray-200 bg-[#F3F6F8] px-3 py-2.5 sm:px-4 sm:py-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 items-center">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-12 w-12 rounded-full bg-gray-300 flex items-center justify-center shrink-0">
                  <span className="text-lg font-semibold text-gray-600">
                    {initial}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm sm:text-base font-semibold text-[#217E90] truncate">
                    {testRecord.patientName}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    Contact :{" "}
                    <span className="text-gray-700">
                      {testRecord.contact || "-"}
                    </span>
                  </p>
                </div>
              </div>

              <div className="text-xs sm:text-sm space-y-1 md:justify-self-center">
                <p className="text-[#217E90]">
                  UHID :{" "}
                  <span className="font-semibold">{testRecord.uhid || "-"}</span>
                </p>
                <p className="text-[#217E90]">
                  Doctor name :{" "}
                  <span className="font-semibold">
                    {testRecord.doctorName || "-"}
                  </span>
                </p>
              </div>

              <div className="text-xs sm:text-sm text-gray-600 space-y-1 md:justify-self-end md:text-right">
                <p>
                  Gender / Age :{" "}
                  <span className="font-medium text-gray-800">
                    {testRecord.genderAge || "-"}
                  </span>
                </p>
                <p>
                  Ref. Doctor :{" "}
                  <span className="font-medium text-gray-800">
                    {testRecord.refDoctor || "-"}
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <div className="overflow-auto max-h-[min(42vh,360px)]">
              <table className="min-w-full text-xs sm:text-sm border-collapse">
                <thead className="sticky top-0 z-[1]">
                  <tr className="bg-[#217E90] text-white">
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">
                      Test Name
                    </th>
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">
                      Subtest Name
                    </th>
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">
                      Parameter Name
                    </th>
                    <th className="px-3 py-2.5 text-left font-semibold w-40 whitespace-nowrap">
                      Results
                    </th>
                    <th className="px-3 py-2.5 text-left font-semibold whitespace-nowrap">
                      Normal Range
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {parameters.length === 0 ? (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-3 py-8 text-center text-gray-500"
                      >
                        No parameters found
                      </td>
                    </tr>
                  ) : (
                    parameters.map((row, index) => {
                      const out = isOutOfRange(row.result, row.normalRange);
                      return (
                        <tr
                          key={row.id}
                          className={
                            out
                              ? "bg-orange-50 border-b border-orange-100"
                              : index % 2 === 0
                                ? "bg-white border-b border-gray-100"
                                : "bg-slate-50/80 border-b border-gray-100"
                          }
                        >
                          <td className="px-3 py-2 text-gray-800 whitespace-nowrap align-middle">
                            {row.testName}
                          </td>
                          <td className="px-3 py-2 text-gray-600 align-middle">
                            {row.subtestName || "-"}
                          </td>
                          <td className="px-3 py-2 text-gray-800 align-middle">
                            {row.parameterName}
                          </td>
                          <td className="px-3 py-2 align-middle">
                            <input
                              type="text"
                              value={row.result}
                              onChange={(e) =>
                                handleResultChange(row.id, e.target.value)
                              }
                              placeholder="Enter result"
                              className={`w-full max-w-[9.5rem] rounded border px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#217E90] ${
                                out
                                  ? "border-orange-400 text-orange-600 font-semibold bg-orange-50"
                                  : "border-gray-300 text-gray-800 bg-white"
                              }`}
                            />
                          </td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap align-middle">
                            {row.normalRange || "-"}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="shrink-0 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            <SearchDropdown
              name="pathologist"
              label="Pathologist name"
              control={control}
              dataArray={PATHOLOGIST_OPTIONS}
              isSearchable
              isClearable
              selectSize="h-9"
              fontSize="text-xs"
            />
            <SearchDropdown
              name="doctor"
              label="Doctor"
              control={control}
              dataArray={DOCTOR_OPTIONS}
              isSearchable
              isClearable
              selectSize="h-9"
              fontSize="text-xs"
            />
            <div className="relative">
              <label className="absolute -top-2 left-2 z-[1] bg-white px-1 text-[10px] text-gray-500">
                Lab ID
              </label>
              <select
                value={labId}
                onChange={(e) => setLabId(e.target.value)}
                className="w-full h-9 rounded border border-gray-300 bg-white px-2 text-xs text-gray-800 focus:outline-none focus:ring-1 focus:ring-[#217E90]"
              >
                {labOptions.map((opt) => (
                  <option key={opt.id || opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="shrink-0 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <textarea
              value={suggestionNote}
              onChange={(e) => setSuggestionNote(e.target.value)}
              placeholder="Suggestion Note"
              rows={3}
              className="w-full min-h-[4.5rem] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#217E90] resize-y"
            />
            <div className="flex flex-col items-start md:items-end gap-2 pb-0.5">
              <CommonCheckbox
                name="digitalSignRequired"
                control={control}
                label="Digital Sign Required"
                fontSize="text-xs"
                checkboxSize="h-4 w-4"
                checkboxColor="bg-[#217E90]"
              />
              <div className="flex items-center gap-2">
                <CommonButton
                  label="Reset"
                  type="button"
                  onClick={handleReset}
                  variant="danger-outline"
                  size="sm"
                  className="px-5"
                  disabled={saving}
                />
                <CommonButton
                  label={saving ? "Saving..." : "Save"}
                  type="button"
                  onClick={handleSave}
                  variant="primary-filled"
                  size="sm"
                  className="px-5 bg-[#217E90] hover:bg-[#1a6575]"
                  disabled={saving}
                />
              </div>
            </div>
          </div>
        </div>
      }
    />
  );
}

export default PathologyResultEntryModal;
